<script setup lang="ts">
import { computed } from 'vue';
import { X } from 'lucide-vue-next';

/**
 * A refusal in the user's terms. Never a raw code — a code on screen is a bug
 * report, not an interface.
 *
 * `role="alert"` so a screen reader announces it when it appears: the user has
 * just pressed something and the answer is here, not where they are looking.
 *
 * **Dismissable when it is a result rather than a condition.** An alert that
 * describes the state of something — a site that is still building, a form
 * that will not submit — goes away when the state does, and a close button on
 * it would be a lie. An alert that reports what just happened has no state
 * behind it and stays until somebody is finished reading it (`D157`).
 *
 * A `div` rather than a `p`, because a result worth reporting is often a count
 * and then a list, and a `p` cannot legally hold one.
 */
const props = withDefaults(
	defineProps<{ tone?: 'warning' | 'info' | 'success'; dismissable?: boolean }>(),
	{ tone: 'warning', dismissable: false },
);

const emit = defineEmits<{ dismiss: [] }>();

const tones = {
	warning: 'border-warning/40 bg-warning-bg text-warning',
	info: 'border-border text-muted',
	success: 'border-success/40 bg-success-bg text-success',
} as const;

const classes = computed(
	() =>
		`rounded-md border px-3 py-2 text-sm ${tones[props.tone]} ${props.dismissable ? 'flex items-start gap-3' : ''}`,
);
</script>

<template>
	<div :class="classes" role="alert">
		<div :class="dismissable ? 'min-w-0 flex-1' : ''"><slot /></div>
		<button
			v-if="dismissable"
			type="button"
			class="-mr-1 shrink-0 rounded p-1 opacity-70 hover:opacity-100"
			aria-label="Dismiss"
			@click="emit('dismiss')"
		>
			<X :size="16" aria-hidden="true" />
		</button>
	</div>
</template>
